// Comptes de présentation sur la page de connexion : hors production, l'API expose les comptes
// fictifs (docs/realisation/comptes-presentation.md) avec leur mot de passe, affiché (D-037). Un clic
// remplit l'identifiant et le mot de passe ; pour un compte à second facteur, un bouton de l'étape
// MFA remplit le code courant. Les comptes du portail renvoient vers la connexion du portail.
import type { CodePresentation, ComptesPresentation as Comptes } from '@eec/contrats';
import { Bouton } from '@eec/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { requete } from '../../api/client';
import { ErreurApi } from '../../api/erreurs';
import styles from './acces.module.css';

/** Liste des comptes ; null tant qu'elle n'est pas connue, [] si l'API n'en expose pas (production). */
export function useComptesPresentation() {
  return useQuery({
    queryKey: ['public', 'comptes-presentation'],
    queryFn: async ({ signal }) => {
      try {
        return await requete<Comptes>('/public/comptes-presentation', { signal });
      } catch (e) {
        if (e instanceof ErreurApi && (e.statut === 404 || e.statut === 0)) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

/** Groupe ou profil des comptes du portail citoyen : ils ne se connectent pas sur cette page. */
export const USAGER_PRESENTATION = /usager|citoyen/i;

export interface ProprietesComptesPresentation {
  onChoisir: (identifiant: string, motDePasse: string, mfa: boolean) => void;
  desactive?: boolean;
}

export function ComptesPresentation({ onChoisir, desactive }: ProprietesComptesPresentation) {
  const comptes = useComptesPresentation();
  const groupes = comptes.data?.groupes ?? [];
  const [groupeCourant, setGroupeCourant] = useState(0);
  if (!comptes.data || groupes.length === 0) return null;
  const groupe = groupes[Math.min(groupeCourant, groupes.length - 1)];
  if (!groupe) return null;
  return (
    <section className={styles.presentation} aria-labelledby="comptes-presentation-titre">
      <h2 id="comptes-presentation-titre" className={styles.presentationTitre}>
        Comptes de présentation
      </h2>
      <p className={styles.aide}>
        Organisations et personnes fictives, avec leur mot de passe. Un clic remplit l&apos;identifiant et le mot de passe ; les comptes du portail se connectent
        sur le portail citoyen.
      </p>
      <div className={styles.groupes} role="tablist" aria-label="Organisation">
        {groupes.map((g, i) => (
          <button
            key={g.titre}
            type="button"
            role="tab"
            aria-selected={i === groupeCourant}
            className={styles.groupeBouton}
            onClick={() => setGroupeCourant(i)}
          >
            {g.code ?? g.titre}
            <span className="cc-masque"> : {g.titre}</span>
          </button>
        ))}
      </div>
      <p className={styles.groupeTitre}>{groupe.titre}</p>
      <ul className={styles.comptes} role="list">
        {groupe.comptes.map((c) => {
          const usager = USAGER_PRESENTATION.test(groupe.titre) || USAGER_PRESENTATION.test(c.profil);
          const details = (
            <>
              <strong>{c.profil}</strong>
              <small>{c.identifiant}</small>
              <small className={styles.motDePasse}>
                Mot de passe : <span>{c.mot_de_passe}</span>
              </small>
              {c.mfa ? <small>Second facteur : code rempli à l&apos;étape suivante</small> : null}
            </>
          );
          return (
            <li key={c.identifiant}>
              {usager ? (
                <div className={styles.compteCarte}>
                  {details}
                  <Link to="/citoyen/connexion" className={styles.compteLien}>
                    Se connecter sur le portail
                  </Link>
                </div>
              ) : (
                <button type="button" className={styles.compteBouton} disabled={desactive} onClick={() => onChoisir(c.identifiant, c.mot_de_passe, c.mfa)}>
                  {details}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Bouton de l'étape MFA : remplit le code à six chiffres du compte de présentation choisi. */
export function BoutonCodePresentation({ identifiant, onCode }: { identifiant: string; onCode: (code: string) => void }) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  return (
    <div className={styles.codePresentation}>
      <Bouton
        type="button"
        variante="secondaire"
        chargement={enCours}
        onClick={() => {
          setEnCours(true);
          setErreur(null);
          requete<CodePresentation>(`/public/comptes-presentation/${encodeURIComponent(identifiant)}/code`)
            .then((r) => onCode(r.code))
            .catch(() => setErreur('Code de présentation indisponible pour ce compte.'))
            .finally(() => setEnCours(false));
        }}
      >
        Remplir le code de présentation
      </Bouton>
      {erreur ? <p className={styles.aide}>{erreur}</p> : null}
    </div>
  );
}
